import React, { useState, useMemo, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { checklistService, serviceService, serviceMaterialService } from '../../../services/supabaseService';
import ChecklistSelectionModal from './ChecklistSelectionModal';
import ServiceMaterialModal from './ServiceMaterialModal';

// Fonctions helper (restent les mêmes)
const getStatusColor = (status) => {
  const colors = {
    'compliant': 'success',
    'non-compliant': 'error',
    'pending': 'warning',
    'draft': 'muted',
    'archived': 'muted',
    'active': 'success',
    'inactive': 'muted'
  };
  return colors[status] || 'muted';
};

const getFrequencyIcon = (frequency) => {
  const icons = {
    'daily': 'Calendar',
    'weekly': 'Calendar',
    'monthly': 'Calendar',
    'quarterly': 'Calendar',
    'annual': 'Calendar',
    'on-demand': 'Clock'
  };
  return icons[frequency] || 'Calendar';
};

const getCategoryLabel = (category) => {
  if (!category) return 'Non catégorisé';
  
  const labels = {
    'sécurité': 'Sécurité',
    'levage': 'Levage',
    'qualité': 'Qualité',
    'maintenance': 'Maintenance',
    'inspection': 'Inspection',
    'audit': 'Audit',
    'test': 'Test',
    'formation': 'Formation'
  };
  return labels[category] || category;
};

const ChecklistDataGrid = ({
  selectedChecklist,
  selectedService,
  onItemSelect,
  selectedItems = [],
  onSelectionChange,
  searchTerm,
  onSearchChange,
  filters,
  onEditItem,
  viewMode,
  currentUserRole,
  currentUserId
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState({
    category: 'all',
    status: 'all',
    frequency: 'all',
    complianceStatus: 'all'
  });
  const [checklists, setChecklists] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // État pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // État pour le modal de sélection des checklists
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [selectedServiceForChecklist, setSelectedServiceForChecklist] = useState(null);
  
  // État pour les checklists détaillés (pour afficher les noms)
  const [detailedChecklists, setDetailedChecklists] = useState([]);

  const [materialsByService, setMaterialsByService] = useState({});
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [selectedServiceForMaterial, setSelectedServiceForMaterial] = useState(null);

  // Récupérer les données depuis Supabase
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (viewMode === 'checklists') {
          // Fetch checklists
          const { data: checklistsData, error: checklistsError } = await checklistService.getAllChecklists();
          
          if (checklistsError) throw checklistsError;

          if (checklistsData) {
            const transformedChecklists = checklistsData.map(checklist => ({
              id: checklist.id,
              type: 'checklist',
              name: checklist.titre || 'N/A',
              code: checklist.code || 'N/A',
              description: checklist.description || '',
              category: checklist.category,
              complianceStatus: checklist.compliance_status || 'pending',
              frequency: checklist.frequency || 'monthly',
              version: checklist.version || '1.0',
              lastUpdated: new Date(checklist.updated_at || checklist.created_at),
              status: checklist.status || 'active',
              itemsCount: checklist.items_count || 0,
              requiredItems: checklist.required_items || 0,
              nextRevisionDate: checklist.next_revision_date ? new Date(checklist.next_revision_date) : null,
              createdBy: checklist.created_by,
              createdAt: new Date(checklist.created_at),
              originalData: checklist
            }));
            setChecklists(transformedChecklists);
          }
        } else {
          // Fetch services
          const { data: servicesData, error: servicesError } = await serviceService.getServices();
          
          if (servicesError) throw servicesError;

          if (servicesData) {
            console.log('Services data fetched:', servicesData);
            // Récupérer tous les checklists pour obtenir leurs noms
            const { data: allChecklistsData } = await checklistService.getAllChecklists();
            setDetailedChecklists(allChecklistsData || []);

            const { data: materialsData, error: materialsError } = await serviceMaterialService.getAll();
            if (materialsError) {
              console.error('Erreur lors du chargement du matériel:', materialsError);
            }
            const materialsMap = {};
            (materialsData || []).forEach((material) => {
              if (!materialsMap[material.service_id]) materialsMap[material.service_id] = [];
              materialsMap[material.service_id].push(material);
            });
            setMaterialsByService(materialsMap);

            const transformedServices = servicesData.map(service => ({
              id: service.id,
              type: 'service',
              name: service.name || 'N/A',
              code: service.code || 'N/A',
              description: service.description || '',
              checklistIds: service.checklists || [], // Tableau d'IDs de checklists
              category: service.equipment?.category?.name || 'inspection',
              duration: service.duration || '1 heure',
              price: service.price || 0,
              frequency: service.frequency || 'monthly',
              complianceStatus: service.compliance_status || 'pending',
              technicianLevel: service.technician_level || 'basic',
              equipmentRequired: service.equipment_required || [],
              status: service.status || 'active',
              createdAt: new Date(service.created_at),
              originalData: service,
              equipment: service.equipment?.name || 'N/A',
              // Ajouter la catégorie directement accessible
              equipmentCategory: service.equipment?.category?.name || 'Non catégorisé'
            }));
            setServices(transformedServices);
          }
        }
        
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [viewMode]);

  // Regrouper les services par nom et équipement
  const groupedServices = useMemo(() => {
    if (viewMode !== 'services') return [];

    // Créer un Map pour regrouper par clé unique (nom_service + equipment)
    const groupedMap = new Map();

    services.forEach(service => {
      // Créer une clé unique combinant le nom du service et l'équipement
      const key = `${service.name}|${service.equipment}`;
      
      if (!groupedMap.has(key)) {
        // Si c'est la première occurrence, créer le groupe
        groupedMap.set(key, {
          id: key,
          name: service.name,
          equipment: service.equipment,
          category: service.equipmentCategory, // Utiliser la catégorie de l'équipement
          type: 'service',
          // Conserver toutes les catégories pour ce groupe
          categories: service.equipmentCategory ? [service.equipmentCategory] : [],
          // Compter le nombre de services dans ce groupe
          count: 1,
          // Garder les IDs pour référence
          serviceIds: [service.id],
          // Garder les données des checklists pour ce groupe
          checklistIds: [...(service.checklistIds || [])],
          // Garder les données d'un service représentatif pour les actions
          representativeService: service
        });
      } else {
        // Si le groupe existe déjà, mettre à jour
        const existingGroup = groupedMap.get(key);
        existingGroup.count += 1;
        existingGroup.serviceIds.push(service.id);
        
        // Ajouter les nouvelles catégories si elles sont différentes et existent
        if (service.equipmentCategory && !existingGroup.categories.includes(service.equipmentCategory)) {
          existingGroup.categories.push(service.equipmentCategory);
        }
        
        // Fusionner les checklistIds sans doublons
        if (service.checklistIds) {
          service.checklistIds.forEach(id => {
            if (!existingGroup.checklistIds.includes(id)) {
              existingGroup.checklistIds.push(id);
            }
          });
        }
        
        groupedMap.set(key, existingGroup);
      }
    });

    // Convertir le Map en tableau
    return Array.from(groupedMap.values());
  }, [services, viewMode]);

  // Fonction pour obtenir le texte de la catégorie à afficher
  const getCategoryDisplay = (group) => {
    // Vérifier si group et group.categories existent
    if (!group) return 'Non catégorisé';
    
    const categories = group.categories || [];
    
    if (categories.length === 0) {
      return 'Non catégorisé';
    } else if (categories.length === 1) {
      return getCategoryLabel(categories[0]);
    } else {
      // S'il y a plusieurs catégories, les afficher toutes
      return categories.map(cat => getCategoryLabel(cat)).join(', ');
    }
  };

  // Fonction pour obtenir les noms des checklists à partir de leurs IDs
  const getChecklistNames = (checklistIds = []) => {
    if (!checklistIds || !checklistIds.length || !detailedChecklists || !detailedChecklists.length) {
      return [];
    }
    
    return checklistIds
      .map(id => detailedChecklists.find(c => c && c.id === id))
      .filter(Boolean)
      .map(checklist => checklist.titre || checklist.name || 'N/A');
  };

  // Fonction pour formater l'affichage des noms de checklists
  const formatChecklistNames = (checklistIds = []) => {
    if (!checklistIds) return 'Aucun checklist associé';
    
    const names = getChecklistNames(checklistIds);
    
    if (names.length === 0) {
      return 'Aucun checklist associé';
    }
    
    if (names.length <= 2) {
      return names.join(', ');
    }
    
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} autres`;
  };

  // Fonction pour ouvrir le modal de sélection des checklists
  const handleOpenChecklistModal = (group) => {
    if (group && group.representativeService) {
      setSelectedServiceForChecklist(group.representativeService);
      setIsChecklistModalOpen(true);
    }
  };

  const handleOpenMaterialModal = (group) => {
    if (group && group.representativeService) {
      setSelectedServiceForMaterial(group.representativeService);
      setIsMaterialModalOpen(true);
    }
  };

  const handleMaterialsChange = (serviceId, nextMaterials) => {
    setMaterialsByService((prev) => ({ ...prev, [serviceId]: nextMaterials }));
  };

  const formatMaterialNames = (materials = []) => {
    if (!materials.length) return 'Aucun matériel';
    const names = [...materials]
      .sort((a, b) => Number(!!b.is_default) - Number(!!a.is_default))
      .map((m) => (m.is_default ? `${m.nom} (par défaut)` : m.nom));
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} autres`;
  };

  // Fonction pour sauvegarder les checklists sélectionnés
  const handleSaveChecklists = async (selectedChecklistIds) => {
    try {
      if (!selectedServiceForChecklist) return;

      // Mettre à jour le service dans la base de données
      const { error } = await serviceService.updateService(
        selectedServiceForChecklist.id,
        { checklists: selectedChecklistIds }
      );

      if (error) throw error;

      // Mettre à jour l'état local
      setServices(prevServices => 
        prevServices.map(service => 
          service.id === selectedServiceForChecklist.id 
            ? { 
                ...service, 
                checklistIds: selectedChecklistIds,
                originalData: {
                  ...service.originalData,
                  checklists: selectedChecklistIds
                }
              }
            : service
        )
      );

    } catch (err) {
      console.error('Erreur lors de la mise à jour des checklists:', err);
      alert('Erreur lors de la mise à jour des checklists');
    }
  };

  // Combiner les données selon le mode de vue
  const gridData = useMemo(() => {
    return viewMode === 'checklists' ? checklists : groupedServices;
  }, [viewMode, checklists, groupedServices]);

  const categoryOptions = useMemo(() => {
    const values = new Set();
    gridData.forEach((item) => {
      if (!item) return;
      if (viewMode === 'services') {
        (item.categories || []).forEach((cat) => cat && values.add(cat));
      } else if (item.category) {
        values.add(item.category);
      }
    });
    return Array.from(values).sort((a, b) =>
      getCategoryLabel(a).localeCompare(getCategoryLabel(b), 'fr')
    );
  }, [gridData, viewMode]);

  useEffect(() => {
    if (filterConfig.category !== 'all' && !categoryOptions.includes(filterConfig.category)) {
      setFilterConfig((prev) => ({ ...prev, category: 'all' }));
    }
  }, [categoryOptions, filterConfig.category]);

  const filteredData = useMemo(() => {
    let filtered = gridData;

    // Filtrer les éléments undefined ou null
    filtered = filtered.filter(item => item != null);

    // Filter by search term
    if (searchTerm && searchTerm.trim() !== '') {
      filtered = filtered.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (item.name && item.name.toLowerCase().includes(searchLower)) ||
          (viewMode === 'services' && item.equipment && item.equipment.toLowerCase().includes(searchLower)) ||
          (viewMode === 'services' && getCategoryDisplay(item).toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply filters
    if (filterConfig.category !== 'all') {
      filtered = filtered.filter((item) => {
        if (viewMode === 'services') {
          // Pour les services groupés, vérifier si la catégorie filtrée est dans les catégories du groupe
          const categories = item.categories || [];
          return categories.some(cat => cat === filterConfig.category);
        } else {
          return item.category === filterConfig.category;
        }
      });
    }
    if (filterConfig.status !== 'all') {
      filtered = filtered.filter((item) => item.status === filterConfig.status);
    }
    if (filterConfig.frequency !== 'all') {
      filtered = filtered.filter((item) => item.frequency === filterConfig.frequency);
    }
    if (filterConfig.complianceStatus !== 'all') {
      filtered = filtered.filter((item) => item.complianceStatus === filterConfig.complianceStatus);
    }

    return filtered;
  }, [gridData, searchTerm, filterConfig, viewMode]);

  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  // Calculer les données paginées
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  // Calculer le nombre total de pages
  useEffect(() => {
    const pages = Math.ceil(sortedData.length / itemsPerPage);
    setTotalPages(pages || 1);
    
    if (currentPage > pages && pages > 0) {
      setCurrentPage(1);
    }
  }, [sortedData, itemsPerPage, currentPage]);

  // Fonctions de tri et sélection
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(paginatedData);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectItem = (item, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, item]);
    } else {
      onSelectionChange(selectedItems.filter((selected) => selected.id !== item.id));
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  const getStatusBadge = (status) => {
    const color = getStatusColor(status);
    const labels = {
      'compliant': 'Conforme',
      'non-compliant': 'Non Conforme',
      'pending': 'En Attente',
      'draft': 'Brouillon',
      'archived': 'Archivé',
      'active': 'Actif',
      'inactive': 'Inactif'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border bg-${color}/10 text-${color} border-${color}/20`}>
        {labels[status] || status}
      </span>
    );
  };

  const getFrequencyBadge = (frequency) => {
    const labels = {
      'daily': 'Quotidien',
      'weekly': 'Hebdomadaire',
      'monthly': 'Mensuel',
      'quarterly': 'Trimestriel',
      'annual': 'Annuel',
      'on-demand': 'Sur Demande'
    };

    return (
      <div className="flex items-center space-x-1">
        <Icon name={getFrequencyIcon(frequency)} size={12} className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          {labels[frequency] || frequency}
        </span>
      </div>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 1) return 'Aujourd\'hui';
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const allSelected = paginatedData.length > 0 && selectedItems.length === paginatedData.length;
  const someSelected = selectedItems.length > 0 && selectedItems.length < paginatedData.length;

  if (loading) {
    return (
      <div className="h-full bg-card flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-card flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-error mb-4" />
          <p className="text-error mb-2">{error}</p>
          <Button 
            variant="outline" 
            onClick={() => window.location.reload()}
          >
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full bg-card flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {viewMode === 'checklists' ? 'Gestion des points de contrôle' : 'Association des points de contrôle aux services'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {sortedData.length} {viewMode === 'checklists' ? 'points de contrôle' : 'groupes de services'} trouvés
                {totalPages > 1 && ` • Page ${currentPage} sur ${totalPages}`}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {viewMode === 'services' && (
                <Button 
                  variant="default" 
                  iconName="Plus" 
                  iconPosition="left" 
                  iconSize={16}
                  onClick={() => onEditItem && onEditItem({ type: 'service' })}
                >
                  Nouveau service
                </Button>
              )}
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                type="search"
                placeholder={
                  viewMode === 'checklists' 
                    ? "Rechercher des checklists par nom, code ou description..." 
                    : "Rechercher des services par nom, équipement ou catégorie..."
                }
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full"
              />
            </div>
            
            <select
              value={filterConfig.category}
              onChange={(e) => setFilterConfig((prev) => ({ ...prev, category: e.target.value }))}
              className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">Toutes les catégories</option>
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>
                  {getCategoryLabel(cat)}
                </option>
              ))}
            </select>

            <select
              value={filterConfig.complianceStatus}
              onChange={(e) => setFilterConfig((prev) => ({ ...prev, complianceStatus: e.target.value }))}
              className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">Tous les statuts</option>
              <option value="compliant">Conforme</option>
              <option value="non-compliant">Non Conforme</option>
              <option value="pending">En Attente</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                <th className="w-12 p-3 text-left">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th className="p-3 text-left">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('name')}
                    className="h-auto p-0 font-medium text-foreground hover:text-primary"
                  >
                    {viewMode === 'checklists' ? 'Checklist' : 'Service'}
                    <Icon
                      name={sortConfig.key === 'name' && sortConfig.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                      size={14}
                      className="ml-1"
                    />
                  </Button>
                </th>
                {viewMode === 'services' && (
                  <th className="p-3 text-left">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort('equipment')}
                      className="h-auto p-0 font-medium text-foreground hover:text-primary"
                    >
                      Équipement
                      <Icon
                        name={sortConfig.key === 'equipment' && sortConfig.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                        size={14}
                        className="ml-1"
                      />
                    </Button>
                  </th>
                )}               
                {viewMode === 'services' && (
                  <th className="p-3 text-left">Catégorie</th>
                )}
                 
                {viewMode === 'services' && (
                  <th className="p-3 text-left">Points de contrôle associés</th>
                )}
                {viewMode === 'services' && (
                  <th className="p-3 text-left">Matériel</th>
                )}
               
                <th className="w-12 p-3"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item) => {
                if (!item) return null;
                
                const isSelected = selectedItems.some((selected) => selected && selected.id === item.id);
                const isCurrentItem = selectedChecklist?.id === item.id || selectedService?.id === item.id;

                return (
                  <tr
                    key={item.id || Math.random()}
                    className={`border-b border-border hover:bg-muted/50 cursor-pointer transition-micro ${
                      isCurrentItem ? 'bg-primary/5' : ''
                    }`}
                  >
                    <td className="p-3">
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectItem(item, e.target.checked);
                        }}
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon 
                            name={item.type === 'checklist' ? 'ClipboardCheck' : 'Wrench'} 
                            size={16} 
                            className="text-primary" 
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{item.name || 'N/A'}</p>
                          {viewMode === 'services' && item.count > 1 && (
                            <p className="text-xs text-muted-foreground">
                              {item.count} service{item.count > 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-foreground">{item.equipment || 'N/A'}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-foreground">
                        {getCategoryDisplay(item)}
                      </span>
                    </td>
                     
                    {viewMode === 'services' && (
                      <td className="p-3">
                        <div className="max-w-[250px]">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm text-foreground truncate">
                              {formatChecklistNames(item.checklistIds)}
                            </p>
                            <span className="text-xs px-2 py-1 bg-muted rounded-full">
                              {(item.checklistIds && item.checklistIds.length) || 0} point{(item.checklistIds && item.checklistIds.length) !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenChecklistModal(item);
                            }}
                            className="w-full text-xs"
                          >
                            <Icon name="Link" size={12} className="mr-1" />
                            Gérer les points de contrôle
                          </Button>
                        </div>
                      </td>
                    )}

                    {viewMode === 'services' && (() => {
                      const materials = materialsByService[item.representativeService?.id] || [];
                      return (
                        <td className="p-3">
                          <div className="max-w-[250px]">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="text-sm text-foreground truncate">
                                {formatMaterialNames(materials)}
                              </p>
                              <span className="text-xs px-2 py-1 bg-muted rounded-full whitespace-nowrap">
                                {materials.length} matériel{materials.length > 1 ? 's' : ''}
                              </span>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMaterialModal(item);
                              }}
                              className="w-full text-xs"
                            >
                              <Icon name="Package" size={12} className="mr-1" />
                              Gérer le matériel
                            </Button>
                          </div>
                        </td>
                      );
                    })()}
                     
                    <td className="p-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditItem && onEditItem(item.representativeService || item);
                        }}
                      >
                        <Icon name="MoreVertical" size={16} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {sortedData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon 
                name={viewMode === 'checklists' ? 'ClipboardCheck' : 'Wrench'} 
                size={48} 
                className="text-muted-foreground mb-4" 
              />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {viewMode === 'checklists' ? 'Aucun point contrôle trouvé' : 'Aucun groupe de services trouvé'}
              </h3>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                {searchTerm 
                  ? 'Essayez d\'ajuster vos critères de recherche ou vos filtres.' 
                  : `Commencez par créer votre premier ${viewMode === 'checklists' ? 'point contrôle' : 'service'}.`}
              </p>
            </div>
          ) : (
            // Pagination Controls
            <div className="sticky bottom-0 bg-card border-t border-border">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Éléments par page:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(e.target.value)}
                      className="px-2 py-1 text-sm bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  
                  <span className="text-sm text-muted-foreground">
                    Affichage {(currentPage - 1) * itemsPerPage + 1}-
                    {Math.min(currentPage * itemsPerPage, sortedData.length)} sur {sortedData.length} éléments
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* Bouton précédent */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="h-8 w-8 p-0"
                  >
                    <Icon name="ChevronLeft" size={16} />
                  </Button>
                  
                  {/* Numéros de page */}
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`dots-${index}`} className="px-2 text-muted-foreground">
                        ...
                      </span>
                    ) : (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="h-8 w-8 p-0"
                      >
                        {page}
                      </Button>
                    )
                  ))}
                  
                  {/* Bouton suivant */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 p-0"
                  >
                    <Icon name="ChevronRight" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {selectedItems.length > 0 && (
          <div className="p-4 border-t border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">
                {selectedItems.length} {viewMode === 'checklists' ? 'checklist(s)' : 'groupe(s) de services'} sélectionné(s)
              </span>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" iconName="Copy" iconPosition="left" iconSize={14}>
                  Dupliquer
                </Button>
                <Button variant="outline" size="sm" iconName="Archive" iconPosition="left" iconSize={14}>
                  Archiver
                </Button>
                <Button variant="outline" size="sm" iconName="Download" iconPosition="left" iconSize={14}>
                  Exporter
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de sélection des checklists */}
      <ChecklistSelectionModal
        isOpen={isChecklistModalOpen}
        onClose={() => setIsChecklistModalOpen(false)}
        onSave={handleSaveChecklists}
        serviceId={selectedServiceForChecklist?.id}
        currentSelectedChecklistIds={selectedServiceForChecklist?.checklistIds || []}
        title={`Associer des points de contrôle à "${selectedServiceForChecklist?.name || ''}"`}
      />

      <ServiceMaterialModal
        isOpen={isMaterialModalOpen}
        onClose={() => setIsMaterialModalOpen(false)}
        service={selectedServiceForMaterial}
        onMaterialsChange={handleMaterialsChange}
      />
    </>
  );
};

export default ChecklistDataGrid;